/**
 * Utility for handling authentication redirects with return URLs
 * 
 * This utility helps manage redirects when users need to authenticate
 * before accessing a protected resource. It stores the intended destination
 * and restores it after successful authentication.
 */

const AUTH_RETURN_URL_KEY = 'authReturnUrl';

/**
 * Store a return URL to redirect to after authentication
 * @param returnUrl The URL to redirect to after login (can include query params)
 */
export function storeAuthReturnUrl(returnUrl: string): void {
    localStorage.setItem(AUTH_RETURN_URL_KEY, returnUrl);
}

/**
 * Get and clear the stored return URL
 * @returns The stored return URL or null if none exists
 */
export function getAndClearAuthReturnUrl(): string | null {
    const returnUrl = localStorage.getItem(AUTH_RETURN_URL_KEY);
    if (returnUrl) {
        localStorage.removeItem(AUTH_RETURN_URL_KEY);
    }
    return returnUrl;
}

/**
 * Get the stored return URL without clearing it
 * @returns The stored return URL or null if none exists
 */
export function getAuthReturnUrl(): string | null {
    return localStorage.getItem(AUTH_RETURN_URL_KEY);
}

/**
 * Clear the stored return URL
 */
export function clearAuthReturnUrl(): void {
    localStorage.removeItem(AUTH_RETURN_URL_KEY);
}

/**
 * Build a return URL with query parameters
 * @param pathname The base path (e.g., '/notifications')
 * @param params Object with query parameters
 * @returns Full URL with query string
 */
export function buildReturnUrl(pathname: string, params: Record<string, string>): string {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value) {
            urlParams.set(key, value);
        }
    });
    return `${pathname}?${urlParams.toString()}`;
}

/**
 * Parse a return URL and extract query parameters
 * @param returnUrl The return URL to parse
 * @returns Object with parsed query parameters
 */
export function parseReturnUrl(returnUrl: string): { pathname: string; params: URLSearchParams } {
    const [pathname, queryString] = returnUrl.split('?');
    const params = new URLSearchParams(queryString || '');
    return { pathname, params };
}

/**
 * Check if a return URL contains a specific parameter
 * @param returnUrl The return URL to check
 * @param paramName The parameter name to check for
 * @returns True if the parameter exists
 */
export function returnUrlHasParam(returnUrl: string, paramName: string): boolean {
    const { params } = parseReturnUrl(returnUrl);
    return params.has(paramName);
}

/**
 * Get a parameter value from a return URL
 * @param returnUrl The return URL to parse
 * @param paramName The parameter name
 * @returns The parameter value or null
 */
export function getReturnUrlParam(returnUrl: string, paramName: string): string | null {
    const { params } = parseReturnUrl(returnUrl);
    return params.get(paramName);
}

